import { FC, useMemo } from 'react';
import { Box, Heading, SimpleGrid, Stack } from '@chakra-ui/react';
import { Loader } from '@netiva/classifieds-ui';

import { useAppSelector } from '@/store';
import { AdAttribute, AdFormData, AdStep as StepType, AdStepItem } from '@/store/ad/types';
import { StepComponent } from './StepComponent';
import { useAdAttributes } from '../hooks';
import { getStepTrackingAction } from '@/hooks';
import { StepComponents } from './StepComponent/config';
import { AdComponentType } from '../types';

export type StepProps = {
  step: StepType;
};

const checkConditions = (attribute: AdAttribute, attributes: AdAttribute[], formData: AdFormData) => {
  if (attribute.conditions?.length) {
    for (let i = 0; i < attribute.conditions.length; i++) {
      const condition = attribute.conditions[i];
      const other = attributes.find((x) => x.name === condition.attributeKey);
      if (!other) {
        continue;
      }

      const dataValue = formData[other.id];
      if (
        dataValue &&
        ((dataValue.value && dataValue.value !== condition.value) ||
          (dataValue.entries && !dataValue.entries.some((e) => e === Number(condition.value))))
      ) {
        return false;
      }
    }
  }
  return true;
};

// A LayoutGroup attribute's members join it via their own parentId (the same field the, currently
// unused, cascading-select behavior uses - see AttributeFormControl's requiresParentEntry). Group
// membership is resolved once up front so a member's position in the flat, SortOrder-ordered
// step.items list never matters: it always renders inside its group's wrapper, never inline.
const useGroupMembers = (attributes: AdAttribute[]) =>
  useMemo(() => {
    const byGroupId = new Map<number, AdAttribute[]>();
    for (const attribute of attributes) {
      if (attribute.parentId == null) continue;
      const parent = attributes.find((a) => a.id === attribute.parentId);
      if (parent?.type !== 'LayoutGroup') continue;
      const members = byGroupId.get(parent.id) ?? [];
      members.push(attribute);
      byGroupId.set(parent.id, members);
    }
    for (const members of byGroupId.values()) {
      members.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    const memberIds = new Set(
      Array.from(byGroupId.values())
        .flat()
        .map((a) => a.id)
    );
    return { byGroupId, memberIds };
  }, [attributes]);

export const Step: FC<StepProps> = ({ step }) => {
  const { formData } = useAppSelector((state) => state.ad);
  const { attributes, isLoading } = useAdAttributes();
  const { byGroupId: groupMembersByGroupId, memberIds: groupMemberIds } = useGroupMembers(attributes);

  if (!step?.items) return null;

  const trackingAction = getStepTrackingAction(step.key);

  const findStepItem = (memberAttribute: AdAttribute): AdStepItem | undefined =>
    step.items.find((si) => si.attributeId === memberAttribute.id || si.attributeKey === memberAttribute.key);

  return (
    <>
      <Loader isLoading={isLoading} />
      {step.items.map((item, i) => {
        // check if it is an attribute
        let attribute: AdAttribute | undefined;
        let parentAttribute: AdAttribute | undefined;
        if (item.attributeId || item.attributeKey) {
          if (isLoading) {
            // don't render if the attributes have not been loaded yet
            return null;
          }
          attribute =
            attributes.find((attribute) => attribute.id === item.attributeId) ||
            attributes.find((attribute) => attribute.key === item.attributeKey);
          if (!attribute) {
            console.debug(`Attribute ${item.attributeId || item.attributeKey} not found.`);
          } else if (!checkConditions(attribute, attributes, formData)) {
            return null;
          }

          if (attribute && groupMemberIds.has(attribute.id)) {
            // rendered inside its group's wrapper instead, wherever that group's own item falls
            return null;
          }

          if (attribute?.type === 'LayoutGroup') {
            const customComponentName = attribute.settings.CustomComponent;
            const CustomComponent: AdComponentType | undefined = customComponentName
              ? StepComponents[customComponentName]
              : undefined;
            if (customComponentName && !CustomComponent) {
              console.warn(`CustomComponent ${customComponentName} not found.`);
            }

            const members = groupMembersByGroupId.get(attribute.id) ?? [];
            const isHorizontal = attribute.settings?.GroupLayout === 'Horizontal';
            // members render via AttributeFormControl, which already gives each field its own mb={4} -
            // a horizontal row still needs a column gutter (spacing), but a vertical stack must not add
            // spacing on top of that or gaps double up.
            const Wrapper = isHorizontal ? SimpleGrid : Stack;
            const wrapperProps = isHorizontal ? { columns: members.length || 1, spacing: 4 } : { spacing: 0 };

            return (
              <Box key={i} mb={4}>
                <Heading size="sm" mb={3} pt="15px" pb="10px">
                  {attribute.name}
                </Heading>
                {CustomComponent ? (
                  <CustomComponent
                    attribute={attribute}
                    stepItem={item}
                    trackingAction={trackingAction}
                    members={members}
                  />
                ) : (
                  <Wrapper {...wrapperProps} ps={4}>
                    {members.map((member) => {
                      const memberItem = findStepItem(member);
                      if (!memberItem) return null;
                      return (
                        <StepComponent
                          key={member.id}
                          attribute={member}
                          stepItem={memberItem}
                          trackingAction={trackingAction}
                        />
                      );
                    })}
                  </Wrapper>
                )}
              </Box>
            );
          }

          if (attribute?.parentId) {
            parentAttribute = attributes.find((attr) => attr.id === attribute!.parentId);
          }
        }
        return (
          <StepComponent
            key={i}
            attribute={attribute}
            parentAttribute={parentAttribute}
            stepItem={item}
            trackingAction={trackingAction}
          />
        );
      })}
    </>
  );
};
